import {
  Form,
  FormValueSetter,
  SearchInput,
  SETTER_ACTIONS,
} from "@neru/base/components";
import { useFilter, useNotifications, useTags } from "@neru/base/hooks";
import type { FlatFilterParams } from "@neru/base/hooks/useFilter";
import { useGetProductProgrammingLanguagesQuery } from "@neru/base/hooks/useProducts";
import { type FilterProps } from "@neru/base/types";
import type { NameValueObject, WithChildren } from "@neru/types";
import {
  Badge,
  Column,
  H1,
  Icon,
  type IconType,
  Phrase,
  Row,
  Spacer,
  Spinner,
} from "@neru/ui";
import { classNames, toNameValueObject } from "@neru/utils";
import React, { useEffect } from "react";

enum FILTER_VIEWS {
  name = "name",
  programmingLanguage = "programming_language",
  tag = "ProductVersion.VersionTags.Tag.name",
}

interface FilterByItemProps {
  FormWrapper?: (props: WithChildren) => React.ReactNode;
  flatFilter?: FlatFilterParams<FormValues>;
  hasProductsListingError?: boolean;
  withForm: boolean;
}

interface FilterListProps extends FilterByItemProps {
  data: Array<NameValueObject<string>>;
  icon: IconType;
  loading: boolean;
  name: FILTER_VIEWS;
}

interface FormValues {
  [FILTER_VIEWS.tag]: Array<string>;
  [FILTER_VIEWS.programmingLanguage]: Array<string>;
  [FILTER_VIEWS.name]: string;
}

const FILTER_RULES = {
  every: [FILTER_VIEWS.tag],
  ilike: [FILTER_VIEWS.name],
  some: [FILTER_VIEWS.programmingLanguage],
};

const FilterList = ({
  data,
  flatFilter,
  FormWrapper = React.Fragment,
  icon,
  loading,
  name,
}: FilterListProps) => {
  const hasItem = (item: string) => flatFilter?.[name]?.includes(item);

  return (
    <FormWrapper>
      <Icon
        className="mr-5 opacity-40"
        connotation="cta"
        customSize={20}
        type={icon}
      />

      <FormValueSetter name={name} submit>
        <Badge
          className={classNames(
            flatFilter?.[name] ? "bg-cta-100" : "bg-cta-300",
            "text-cta-800 fill-cta-800",
          )}
          connotation="cta"
          icon={flatFilter?.[name] ? undefined : "check-circle-line"}
          layout={flatFilter?.[name] ? "soft" : "filled"}
        >
          {name === FILTER_VIEWS.tag ? "All products" : "All languages"}
        </Badge>
      </FormValueSetter>
      <Spacer size="xs" />

      {loading && (
        <Column className="px-4">
          <Spinner data-testid={`filter-${name}`} size={-6} />
        </Column>
      )}

      {data.map(({ value }) => (
        <div className="mx-1" key={value}>
          <FormValueSetter
            action={
              hasItem(value) ? SETTER_ACTIONS.remove : SETTER_ACTIONS.insert
            }
            name={name}
            submit
            value={value}
          >
            <Badge
              className={classNames(
                hasItem(value) ? "bg-cta-300" : "bg-cta-100",
                "text-cta-800 fill-cta-800",
              )}
              connotation="cta"
              icon={hasItem(value) ? "check-circle-line" : undefined}
              layout={hasItem(value) ? "filled" : "soft"}
            >
              {value}
            </Badge>
          </FormValueSetter>
        </div>
      ))}
    </FormWrapper>
  );
};

const FilterByTags = ({
  flatFilter,
  FormWrapper = React.Fragment,
  hasProductsListingError,
}: FilterByItemProps) => {
  const { data, error, loading } = useTags();
  const { showError } = useNotifications();

  useEffect(() => {
    if (error && !hasProductsListingError) {
      showError("Failed to get filter options. Retrying...");
    }
  }, [error, hasProductsListingError, showError]);

  const list = data?.Tags?.map(({ name }) => toNameValueObject(name)) ?? [];

  return data?.Tags.length ? (
    <FormWrapper>
      <Row className="mt-3 h-6 items-center">
        <FilterList
          data={list}
          flatFilter={flatFilter}
          icon="packet-2-solid"
          loading={loading}
          name={FILTER_VIEWS.tag}
          withForm
        />
      </Row>
    </FormWrapper>
  ) : null;
};

const FilterByProgrammingLanguage = ({
  flatFilter,
  FormWrapper = React.Fragment,
  hasProductsListingError,
}: FilterByItemProps) => {
  const { data, error, loading } = useGetProductProgrammingLanguagesQuery();
  const { showError } = useNotifications();

  useEffect(() => {
    if (error && !hasProductsListingError) {
      showError("Failed to get filter options. Retrying...");
    }
  }, [error, hasProductsListingError, showError]);

  const list = data?.map(({ value }) => toNameValueObject(value)) ?? [];

  return data?.length ? (
    <FormWrapper>
      <Row className="mt-3 h-6 items-center">
        <FilterList
          data={list}
          flatFilter={flatFilter}
          icon="code-solid"
          loading={loading}
          name={FILTER_VIEWS.programmingLanguage}
          withForm
        />
      </Row>
    </FormWrapper>
  ) : null;
};

export const Filter = ({
  children,
  hasProductsListingError,
  onFilter,
}: FilterProps & WithChildren & { hasProductsListingError: boolean }) => {
  const [filter, setValues, flatFilter] = useFilter<FormValues>(FILTER_RULES);

  useEffect(() => {
    if (filter) {
      if (filter.name) {
        const { name, ...rest } = filter;

        onFilter({
          _and: {
            _and: {
              _and: {
                ...rest,
              },
              _or: [
                { name },
                {
                  ProductVersions: {
                    long_description: name,
                  },
                },
                {
                  ProductVersions: {
                    short_description: name,
                  },
                },
              ],
            },
          },
        });
      } else {
        onFilter({
          _and: {
            _and: {
              ...filter,
            },
          },
        });
      }
    }
  }, [filter, onFilter]);

  return (
    <Form name="products" onFinish={setValues}>
      <div
        className={`
          relative box-content flex h-[345px] flex-col items-center
          justify-center overflow-x-clip bg-linear-to-r from-[#8728FB]
          to-[#FFA58B] px-8 py-4
        `}
      >
        <div
          className={`
            absolute mt-80 ml-248 h-20 w-20 rotate-[-22deg] rounded-lg
            bg-[#60EECC] shadow-lg
          `}
        ></div>

        <div
          className={`
            absolute mt-8 -ml-192 h-5 w-5 -rotate-6 rounded bg-[#FA7454]
            shadow-xl
          `}
        ></div>

        <div
          className={`
            shadow-block absolute mt-60 -ml-208 flex h-12 w-12 -rotate-6
            items-center justify-center rounded-md bg-white text-lg
          `}
        >
          🤖
        </div>

        <div
          className={`
            absolute mt-72 -ml-340 h-[1.2rem] w-[1.2rem] rotate-[-25deg] rounded
            bg-[#FB8FD8]
          `}
        ></div>

        <div
          className={`
            absolute mt-40 -ml-248 h-16 w-16 rotate-15 rounded-md bg-[#FACC4B]
            shadow-xl
          `}
        ></div>

        <div
          className={`
            absolute mt-52 ml-196 h-6 w-6 -rotate-12 rounded bg-[#BB1E89]
          `}
        ></div>

        <div
          className={`
            absolute mt-24 ml-216 h-10 w-10 rotate-30 rounded-md bg-[#FB8FD8]
            shadow-lg
          `}
        ></div>

        <div
          className={`
            absolute -mt-44 ml-240 h-[1.1rem] w-[1.1rem] rotate-12 rounded
            bg-[#9941FF]
          `}
        ></div>

        <div
          className={`
            shadow-block absolute -mt-64 ml-216 flex h-[2.7rem] w-[2.7rem]
            rotate-[-18deg] items-center justify-center rounded-md bg-white
            text-lg
          `}
        >
          🚀
        </div>

        <div
          className={`absolute -mt-72 ml-104 h-6 w-6 rounded bg-[#440291]`}
        ></div>

        <div
          className={`
            absolute mt-92 ml-64 h-6 w-6 rotate-25 rounded bg-[#60CCEE]
            shadow-md
          `}
        ></div>

        <div
          className={`
            absolute mt-72 ml-20 h-[1.7rem] w-[1.7rem] rotate-[-25deg] rounded
            bg-[#FFEDFB] opacity-30 shadow-lg
          `}
        ></div>

        <H1 className="mt-0 text-[66px] font-normal text-white">
          Code Hub
          <span className="align-super">
            <Badge connotation="success" layout="soft" shape="pill">
              beta
            </Badge>
          </span>
        </H1>
        <Phrase className="text-primary-0 mx-8 px-8 text-center font-bold">
          Get started with pre-built solutions using Vonage's serverless
          platform.
        </Phrase>

        <Row className="mt-2 mb-12 w-full justify-center pt-1">
          <Column className="max-w-[600px] min-w-[400px] grow">
            <SearchInput
              name={FILTER_VIEWS.name}
              placeholder="What do you want to build?"
              value={flatFilter?.name}
            />
          </Column>
        </Row>
      </div>

      <Column
        className={`
          mx-8 mt-6
          sm:mx-16
          md:mx-24
          lg:mx-32
        `}
      >
        <FilterByTags
          flatFilter={flatFilter}
          hasProductsListingError={hasProductsListingError}
          withForm
        />
        <FilterByProgrammingLanguage
          flatFilter={flatFilter}
          hasProductsListingError={hasProductsListingError}
          withForm
        />
      </Column>

      <Row className="mt-8">
        <Column className="grow-2">{children}</Column>
      </Row>
    </Form>
  );
};
